import { useState, useEffect, useMemo } from "react";
import { Checkbox, Col, Form, Input, InputNumber, Row, Select } from "antd";
import "../users/addUser.css";

import { useForm } from "antd/lib/form/Form";
import { submitUserThunk } from "../../../store/thunks/usersThunk";
import AddEditForm from "../../../components/molecules/Forms/addEditForm";
import { useDispatch } from "react-redux";
import secureLocalStorage from "react-secure-storage";
import { roleHierarchyFields } from "../../../utils/Constants";

const AddUser = ({
  editData,
  clients,
  departments,
  roles,
  additionalPermissions,
  statuses,
  allVps,
  allAvps,
  allManagers,
  allAms,
  allTls,
  allAtls,
  onSuccess,
  children,
  canEditUserRole,
  canEditUserStatus,
}) => {
  const dispatch = useDispatch();
  const [form] = useForm();

  const slsLIUId = secureLocalStorage.getItem("lIUId");
  const slsRole = secureLocalStorage.getItem("role");

  const [selectedRole, setSelectedRole] = useState();
  const [profilePicInfo, setProfilePicInfo] = useState("");
  const [errors, setErrors] = useState([]);
  const [profilePicWithUrlChecked, setProfilePicWithUrlChecked] =
    useState(false);
  const [existingImage, setExistingImage] = useState(null);
  const [showFileInput, setShowFileInput] = useState(true);

  const visibleFields = useMemo(() => {
    return roleHierarchyFields[selectedRole] || [];
  }, [selectedRole]);
  const filteredRoles =
    slsRole === process.env.REACT_APP_QC && canEditUserRole
      ? roles.filter((role) => role.value !== process.env.REACT_APP_AROLE)
      : roles;
  useEffect(() => {
    const allHierarchyFields = ["vp", "avp", "manager", "am", "tl", "atl"];

    const hiddenFields = allHierarchyFields.filter(
      (field) => !visibleFields.includes(field),
    );

    const resetObject = {};

    hiddenFields.forEach((field) => {
      resetObject[field] = null;
    });

    form.setFieldsValue(resetObject);
  }, [selectedRole]);

  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    setProfilePicWithUrlChecked(checked);

    if (checked) {
      setProfilePicInfo(null);
      setShowFileInput(false);

      form.setFieldValue("profilePicUrl", "");
    }
  };

  const inputComponent = <Input className="w-100" />;

  const selectedClients = Form.useWatch("client", form) || [];
  const clientOptions = clients.map((client) => {
    const isAllSelected = selectedClients.includes("ALL");

    const hasSpecificSelected = selectedClients.some((item) => item !== "ALL");

    return {
      ...client,

      disabled: client.value === "ALL" ? hasSpecificSelected : isAllSelected,
    };
  });
  const selectedDepartments = Form.useWatch("department", form) || [];
  const departmentOptions = departments.map((department) => {
    const isAllSelected = selectedDepartments.includes("ALL");

    const hasSpecificSelected = selectedDepartments.some(
      (item) => item !== "ALL",
    );

    return {
      ...department,

      disabled:
        department.value === "ALL" ? hasSpecificSelected : isAllSelected,
    };
  });
  const onSubmit = async (values, editData) => {
    const isEdit = Boolean(editData);

    const getBase64ImageExtension = (urlOrBase64) => {
      if (urlOrBase64?.startsWith("data:image")) {
        const mimeTypeMatch = urlOrBase64.match(
          /^data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+).*,.*/,
        );

        if (mimeTypeMatch && mimeTypeMatch[1]) {
          return mimeTypeMatch[1].split("/")[1];
        } else {
          return "";
        }
      } else {
        return urlOrBase64?.split(".").pop().split("?")[0];
      }
    };

    const normalizeId = (val) =>
      typeof val === "string" && val.length === 24 ? val : null;
    const normalizeIds = (vals) => {
      if (!Array.isArray(vals)) return [];

      return vals.filter((val) => typeof val === "string" && val.length === 24);
    };
    const selectedClients = values.client || [];
    const selectedDepartments = values.department || [];
    const clientScope = selectedClients.includes("ALL") ? "ALL" : "LIMITED";

    const departmentScope = selectedDepartments.includes("ALL")
      ? "ALL"
      : "LIMITED";
    const filteredClients =
      clientScope === "ALL"
        ? []
        : normalizeIds(selectedClients.filter((item) => item !== "ALL"));

    const filteredDepartments =
      departmentScope === "ALL"
        ? []
        : normalizeIds(selectedDepartments.filter((item) => item !== "ALL"));
    const payload =
      isEdit && slsRole === process.env.REACT_APP_QC
        ? {
            ...(canEditUserRole && {
              urole: values?.urole,

              vp: normalizeId(values.vp),

              avp: normalizeId(values.avp),

              manager: normalizeId(values.manager),

              am: normalizeId(values.am),

              tl: normalizeId(values.tl),

              atl: normalizeId(values.atl),

              client: filteredClients,

              clientScope,

              department: filteredDepartments,

              departmentScope,

              status: values.status,
            }),

            ...(canEditUserStatus && {
              status: values.status,
            }),
          }
        : {
            empID: parseInt(values?.empID),
            name: values?.name,
            imageName:
              parseInt(values?.empID) +
              "_" +
              values?.name?.replaceAll(" ", "_"),
            email: values?.email,
            urole: values?.urole,
            profilePicUrl:
              profilePicWithUrlChecked === true
                ? values?.profilePicUrl
                : profilePicInfo
                  ? profilePicInfo
                  : null,

            client: filteredClients,
            clientScope,

            department: filteredDepartments,
            departmentScope,
            vp: normalizeId(values.vp),
            avp: normalizeId(values.avp),
            manager: normalizeId(values.manager),
            am: normalizeId(values.am),
            tl: normalizeId(values.tl),
            atl: normalizeId(values.atl),
            uploadType:
              profilePicWithUrlChecked === true ? "thumbUrl" : "other",

            additionalPermissions: values.additionalPermissions || [],

            ext:
              profilePicWithUrlChecked === true
                ? getBase64ImageExtension(values?.profilePicUrl)
                : profilePicInfo
                  ? getBase64ImageExtension(profilePicInfo)
                  : "",

            status: isEdit ? values.status : "ACTIVE",
            historySource: "backend",
            historyAction: isEdit ? "update" : "create",
            historyUserID: slsLIUId,
          };

    if (isEdit) {
      payload.id = editData.key;
    }

    await dispatch(submitUserThunk(payload)).unwrap();
  };

  const getId = (val) => {
    if (!val) return null;

    if (typeof val === "string" && val.length === 24) return val;

    if (typeof val === "object") return val._id;

    return null;
  };

  useEffect(() => {
    if (!editData) return;

    const roleValue =
      editData.role?._id || editData.role || editData.role?.[0]?._id || null;

    form.setFieldsValue({
      empID: editData.empID,
      name: editData.name,
      email: editData.email,
      urole: roleValue,

      client:
        editData.clientScope === "ALL"
          ? ["ALL"]
          : Array.isArray(editData.client)
            ? editData.client.map((client) => client?._id || client)
            : [],
      department:
        editData.departmentScope === "ALL"
          ? ["ALL"]
          : Array.isArray(editData.department)
            ? editData.department.map(
                (department) => department?._id || department,
              )
            : [],
      vp: getId(editData.vp),
      avp: getId(editData.avp),
      manager: getId(editData.manager),
      am: getId(editData.am),
      tl: getId(editData.tl),
      atl: getId(editData.atl),

      additionalPermissions:
        editData.additionalPermissions?.map((p) => p?._id || p) || [],

      status: editData.status,
    });

    setSelectedRole(roleValue);

    if (editData.profilePicUrl) {
      setExistingImage(editData.profilePicUrl);
      setShowFileInput(false);
    }
  }, [editData]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.src = e.target.result;

      img.onload = () => {
        img.src && setProfilePicInfo(img.src);
      };
    };

    reader.readAsDataURL(file);
  };

  return (
    <AddEditForm
      form={form}
      editData={editData}
      onSubmit={onSubmit}
      onSuccess={onSuccess}
      successMessages={{
        create: "User created successfully",
        update: "User updated successfully",
      }}
      fields={
        <>
          <Row className="pb-16" gutter={[12, 12]}>
            {slsRole === process.env.REACT_APP_AROLE && (
              <>
                <Col xs={24} sm={24} md={12} lg={12}>
                  {/* allVps:{allVps && JSON.stringify(allVps,null,2)}
                        <br />allAvps:{allAvps && JSON.stringify(allAvps,null,2)}
                        <br />allManagers:{allManagers && JSON.stringify(allManagers,null,2)}
                        <br />allAms:{allAms && JSON.stringify(allAms,null,2)}
                        <br />tl:{tl && JSON.stringify(tl,null,2)}
                        <br />allAtls:{allAtls && JSON.stringify(allAtls,null,2)} */}
                  <Form.Item
                    className="m-0"
                    label="Employee ID"
                    name="empID"
                    rules={[
                      {
                        required: true,
                        message: "Please enter employee id!",
                      },
                    ]}
                  >
                    <InputNumber
                      placeholder="Enter employee id"
                      className="w-100"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={24} md={12} lg={12}>
                  <Form.Item
                    className="m-0"
                    label="Name"
                    name="name"
                    rules={[
                      {
                        required: true,
                        message: "Please enter the name!",
                      },
                    ]}
                  >
                    <Input placeholder="Enter name" className="w-100" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={24} md={12} lg={12}>
                  <Form.Item
                    className="m-0"
                    label="Email"
                    name="email"
                    rules={[
                      {
                        required: true,
                        message: "Please input your email!",
                      },
                      {
                        type: "email",
                        message: "Please enter a valid email address",
                      },
                    ]}
                  >
                    <Input placeholder="Enter email" className="w-100" />
                  </Form.Item>
                </Col>
              </>
            )}

            {(slsRole === process.env.REACT_APP_AROLE ||
              (slsRole === process.env.REACT_APP_QC && canEditUserRole)) && (
              <Col
                xs={24}
                sm={24}
                md={24}
                lg={slsRole === process.env.REACT_APP_AROLE ? 12 : 24}
              >
                <Form.Item
                  className="m-0"
                  label="Role"
                  name="urole"
                  rules={[
                    {
                      required: true,
                      message: "Please select a role!",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    allowClear
                    options={filteredRoles}
                    onChange={(val) => {
                      setSelectedRole(val);
                    }}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        ?.toLowerCase()
                        .includes(input?.toLowerCase())
                    }
                    placeholder="Select Role"
                  />
                </Form.Item>
              </Col>
            )}
            {visibleFields.includes("vp") && (
              <Col xs={24} sm={24} md={12} lg={12}>
                <Form.Item label="VP" name="vp">
                  <Select
                    showSearch
                    allowClear
                    options={allVps}
                    placeholder="Select VP"
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        ?.toLowerCase()
                        .includes(input?.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
            )}

            {visibleFields.includes("avp") && (
              <Col xs={24} sm={24} md={12} lg={12}>
                <Form.Item label="AVP" name="avp">
                  <Select
                    showSearch
                    allowClear
                    options={allAvps}
                    placeholder="Select AVP"
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        ?.toLowerCase()
                        .includes(input?.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
            )}

            {visibleFields.includes("manager") && (
              <Col xs={24} sm={24} md={12} lg={12}>
                <Form.Item label="Manager" name="manager">
                  <Select
                    showSearch
                    allowClear
                    options={allManagers}
                    placeholder="Select Manager"
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        ?.toLowerCase()
                        .includes(input?.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
            )}
            {visibleFields.includes("am") && (
              <Col xs={24} sm={24} md={12} lg={12}>
                <Form.Item label="AM" name="am">
                  <Select
                    showSearch
                    allowClear
                    options={allAms}
                    placeholder="Select AM"
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        ?.toLowerCase()
                        .includes(input?.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
            )}
            {visibleFields.includes("tl") && (
              <Col xs={24} sm={24} md={12} lg={12}>
                <Form.Item label="TL" name="tl">
                  <Select
                    showSearch
                    allowClear
                    options={allTls}
                    placeholder="Select TL"
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        ?.toLowerCase()
                        .includes(input?.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
            )}

            {visibleFields.includes("atl") && (
              <Col xs={24} sm={24} md={12} lg={12}>
                <Form.Item label="ATL" name="atl">
                  <Select
                    showSearch
                    allowClear
                    options={allAtls}
                    placeholder="Select ATL"
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        ?.toLowerCase()
                        .includes(input?.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
            )}

            <Col xs={12} sm={12} md={12} lg={12}>
              <Form.Item
                label="Client"
                name="client"
                rules={[
                  {
                    required: true,
                    message: "Please select a client!",
                  },
                ]}
              >
                <Select
                  showSearch
                  allowClear
                  mode="multiple"
                  placeholder="Select Clients"
                  options={clientOptions}
                  onChange={(value) => {
                    const actualClientIds = clients
                      .filter((client) => client.value !== "ALL")
                      .map((client) => client.value);
                    const selectedWithoutAll = value.filter(
                      (item) => item !== "ALL",
                    );

                    if (selectedWithoutAll.length === actualClientIds.length) {
                      form.setFieldValue("client", ["ALL"]);

                      return;
                    }
                    // If ALL selected directly
                    if (value.includes("ALL")) {
                      form.setFieldValue("client", ["ALL"]);

                      return;
                    }

                    form.setFieldValue("client", value);
                  }}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      ?.toLowerCase()
                      .includes(input?.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={12} sm={12} md={12} lg={12}>
              <Form.Item
                label="Department"
                name="department"
                rules={[
                  {
                    required: true,
                    message: "Please select a department!",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  showSearch
                  allowClear
                  options={departmentOptions}
                  placeholder="Select Departments"
                  onChange={(value) => {
                    const actualDepartmentIds = departments
                      .filter((department) => department.value !== "ALL")
                      .map((department) => department.value);

                    const selectedWithoutAll = value.filter(
                      (item) => item !== "ALL",
                    );

                    if (
                      selectedWithoutAll.length === actualDepartmentIds.length
                    ) {
                      form.setFieldValue("department", ["ALL"]);

                      return;
                    }

                    if (value.includes("ALL")) {
                      form.setFieldValue("department", ["ALL"]);

                      return;
                    }

                    form.setFieldValue("department", value);
                  }}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      ?.toLowerCase()
                      .includes(input?.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            {slsRole === process.env.REACT_APP_AROLE && (
              <>
                <Col xs={24} sm={24} md={24} lg={24}>
                  <Form.Item
                    label="Additional Permissions"
                    name="additionalPermissions"
                  >
                    <Select
                      mode="multiple"
                      showSearch
                      allowClear
                      options={additionalPermissions}
                      placeholder="Select permissions"
                      allowClear
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          ?.toLowerCase()
                          .includes(input?.toLowerCase())
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={24} lg={24}>
                  <Form.Item label="Profile Image" name="profilePicUrl">
                    {profilePicWithUrlChecked === true ? (
                      inputComponent
                    ) : existingImage && !showFileInput ? (
                      <div style={{ position: "relative", width: 150 }}>
                        <img
                          src={`${process.env.REACT_APP_BACKEND}/images/${existingImage}`}
                          alt="profile"
                          style={{
                            width: "150px",
                            height: "150px",
                            objectFit: "cover",
                            borderRadius: 6,
                            border: "1px solid #ddd",
                          }}
                        />

                        <span
                          onClick={() => {
                            setExistingImage(null);
                            setShowFileInput(true);
                            form.setFieldValue("profilePicUrl", null);
                          }}
                          style={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            background: "#ff4d4f",
                            color: "#fff",
                            borderRadius: "50%",
                            width: 22,
                            height: 22,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            fontSize: 12,
                          }}
                        >
                          ✕
                        </span>
                      </div>
                    ) : (
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="upload-list-block"
                      />
                    )}
                  </Form.Item>

                  <Form.Item label="" name="profilePicUrl">
                    <Checkbox
                      checked={profilePicWithUrlChecked}
                      onChange={handleCheckboxChange}
                      className="form-label-antd profilePicWithUrlCheckBox"
                      name="profilePicWithUrlCheckBox"
                    >
                      Upload with url
                    </Checkbox>
                  </Form.Item>
                </Col>
              </>
            )}
            {editData &&
              (slsRole === process.env.REACT_APP_AROLE ||
                (slsRole === process.env.REACT_APP_QC &&
                  canEditUserStatus)) && (
                <Col xs={24} sm={24} md={24} lg={24}>
                  <Form.Item
                    label="Status"
                    name="status"
                    rules={[
                      {
                        required: true,
                        message: "Please select a status!",
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      allowClear
                      options={statuses}
                      placeholder="Select Status"
                    />
                  </Form.Item>
                </Col>
              )}
          </Row>
        </>
      }
    />
  );
};

export default AddUser;
